import { useNavigate } from 'react-router-dom';
import { Users, ChevronRight, BrainCircuit, Shield, MessageSquare, FolderKanban, Send } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import { SubscriptionPrices, KPIMetrics, KPIDisplaySettings, AdminMessage } from '../models/types';
import ProjectTicker from '../components/ProjectTicker/ProjectTicker';

const Home = () => {
  const { currentUser, userData } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [prices, setPrices] = useState<SubscriptionPrices>({
    normal: { regular: 5 },
    gold: { regular: 20 },
    platinum: { regular: 50 }
  });
  const [kpiMetrics, setKpiMetrics] = useState<KPIMetrics>({
    totalMessages: 0,
    totalProjects: 0,
    totalProposals: 0,
    totalRequesters: 0,
    totalProviders: 0
  });
  const [kpiDisplaySettings, setKpiDisplaySettings] = useState<KPIDisplaySettings>({
    showMessages: true,
    showProjects: true,
    showProposals: true,
    showRequesters: true,
    showProviders: true
  });
  const [loadingKpi, setLoadingKpi] = useState(true);
  const [adminMessages, setAdminMessages] = useState<AdminMessage[]>([]);
  const [projects, setProjects] = useState<{name: string; budget: number; timeline: string}[]>([]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const q = query(
          collection(db, 'projects'),
          where('status', '==', 'active'),
          orderBy('createdAt', 'desc'),
          limit(10)
        );
        const snap = await getDocs(q);
        setProjects(snap.docs.map(d => {
          const data = d.data();
          return { name: data.name || '', budget: data.budget || 0, timeline: data.timeline || '' };
        }));
      } catch { /* non-critical */ }
    };
    fetchProjects();
  }, []);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch prices
        const pricesDoc = await getDocs(collection(db, 'systemParams'));
        const pricesData = pricesDoc.docs.find(doc => doc.id === 'subscriptionPrices');
        if (pricesData) {
          setPrices(pricesData.data() as SubscriptionPrices);
        }

        // Fetch KPI display settings
        const kpiSettingsDoc = await getDoc(doc(db, 'systemParams', 'kpiDisplaySettings'));
        if (kpiSettingsDoc.exists()) {
          setKpiDisplaySettings(kpiSettingsDoc.data() as KPIDisplaySettings);
        }

        const messagesQuery = query(
          collection(db, 'adminMessages'),
          where('isActive', '==', true),
          orderBy('priority', 'desc'),
          limit(3)
        );
        const messagesSnapshot = await getDocs(messagesQuery);
        const messages: AdminMessage[] = messagesSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            title_fr: data.title_fr || '',
            title_en: data.title_en || '',
            message_fr: data.message_fr || '',
            message_en: data.message_en || '',
            imageUrl: data.imageUrl,
            isActive: data.isActive ?? true,
            priority: data.priority || 0,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
            createdBy: data.createdBy || ''
          };
        });
        setAdminMessages(messages);

        // Fetch KPI metrics with simplified approach
        await fetchKpiMetrics();
      } catch (error) {
        console.error('Error fetching data:', error);
        // Continue with default values if fetch fails
      } finally {
        setLoadingKpi(false);
      }
    };

    fetchData();
  }, []);

  const fetchKpiMetrics = async () => {
    try {
      // Use simpler queries that don't require complex permissions
      const [projectsSnapshot, proposalsSnapshot, usersSnapshot] = await Promise.all([
        getDocs(collection(db, 'projects')),
        getDocs(collection(db, 'proposals')),
        getDocs(collection(db, 'users'))
      ]);

      // Count projects
      const totalProjects = projectsSnapshot.size;

      // Count proposals
      const totalProposals = proposalsSnapshot.size;

      // Count users by role
      let totalRequesters = 0;
      let totalProviders = 0;
      
      usersSnapshot.docs.forEach(doc => {
        const userData = doc.data();
        if (userData.role === 'requester') {
          totalRequesters++;
        } else if (userData.role === 'provider') {
          totalProviders++;
        }
      });

      // For messages, we'll use a simplified count or skip if too complex
      let totalMessages = 0;
      try {
        // Try to count messages from a few recent projects only
        const recentProjects = projectsSnapshot.docs.slice(0, 10); // Limit to 10 projects
        for (const projectDoc of recentProjects) {
          try {
            const chatsSnapshot = await getDocs(collection(db, `projects/${projectDoc.id}/chats`));
            for (const chatDoc of chatsSnapshot.docs) {
              const messagesSnapshot = await getDocs(collection(db, `projects/${projectDoc.id}/chats/${chatDoc.id}/messages`));
              totalMessages += messagesSnapshot.size;
            }
          } catch (chatError) {
            // Skip this project's messages if there's an error
            continue;
          }
        }
      } catch (messageError) {
        console.warn('Could not fetch message count:', messageError);
        // Use a placeholder value or skip messages
        totalMessages = 0;
      }

      setKpiMetrics({
        totalMessages,
        totalProjects,
        totalProposals,
        totalRequesters,
        totalProviders
      });
    } catch (error) {
      console.error('Error fetching KPI metrics:', error);
      // Set default values if fetch fails
      setKpiMetrics({
        totalMessages: 0,
        totalProjects: 0,
        totalProposals: 0,
        totalRequesters: 0,
        totalProviders: 0
      });
    }
  };
  
  // Redirect to appropriate dashboard if already logged in
  const goToDashboard = () => {
    if (userData?.role === 'admin') {
      navigate('/admin/dashboard');
    } else if (userData?.role === 'requester') {
      navigate('/requester/dashboard');
    } else if (userData?.role === 'provider') {
      navigate('/provider/dashboard');
    }
  };

  const hasSpecialOffer = (planPrices: { regular: number; promotional?: number }) => {
    return planPrices.promotional === 0;
  };

  // Get visible KPIs for display
  const getVisibleKpis = () => {
    const kpis = [];
    
    if (kpiDisplaySettings.showMessages && kpiMetrics.totalMessages > 0) {
      kpis.push({
        icon: MessageSquare,
        label: 'Messages',
        value: kpiMetrics.totalMessages,
        color: 'text-blue-600'
      });
    }
    
    if (kpiDisplaySettings.showProjects) {
      kpis.push({
        icon: FolderKanban,
        label: 'Projects',
        value: kpiMetrics.totalProjects,
        color: 'text-green-600'
      });
    }
    
    if (kpiDisplaySettings.showProposals) {
      kpis.push({
        icon: Send,
        label: 'Proposals',
        value: kpiMetrics.totalProposals,
        color: 'text-purple-600'
      });
    }
    
    if (kpiDisplaySettings.showRequesters) {
      kpis.push({
        icon: Users,
        label: 'Requesters',
        value: kpiMetrics.totalRequesters,
        color: 'text-orange-600'
      });
    }
    
    if (kpiDisplaySettings.showProviders) {
      kpis.push({
        icon: Users,
        label: 'Providers',
        value: kpiMetrics.totalProviders,
        color: 'text-indigo-600'
      });
    }
    
    return kpis;
  };

  const visibleKpis = getVisibleKpis();

  return (
    <div className="min-h-screen bg-white">
      {adminMessages.length > 0 && (
        <section className="bg-gradient-to-r from-blue-50 via-white to-primary-50 py-8 md:py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto space-y-6 md:space-y-8">
              {adminMessages.map((message) => {
                const title = t('language') === 'fr' ? message.title_fr : message.title_en;
                const content = t('language') === 'fr' ? message.message_fr : message.message_en;

                return (
                  <div
                    key={message.id}
                    className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-shadow duration-300"
                  >
                    <div className="grid md:grid-cols-2 gap-0">
                      {message.imageUrl && (
                        <div className="relative h-64 md:h-auto min-h-[300px] order-2 md:order-1">
                          <img
                            src={message.imageUrl}
                            alt={title}
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent md:hidden"></div>
                        </div>
                      )}

                      <div className={`p-6 md:p-8 lg:p-10 flex flex-col justify-center order-1 md:order-2 ${!message.imageUrl ? 'md:col-span-2' : ''}`}>
                        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                          {title}
                        </h2>

                        <p className="text-base md:text-lg text-gray-700 leading-relaxed whitespace-pre-line">
                          {content}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Hero Section */}
      <ProjectTicker />
      {projects.length > 0 && (
        <div style={{width:'100%', background:'#fff', border:'1px solid #e5e5e5', borderRadius:'12px', margin:'2rem 0', overflow:'hidden'}}>
          <div style={{display:'flex', alignItems:'center', gap:'10px', padding:'14px 20px', borderBottom:'1px solid #e5e5e5'}}>
            <div style={{width:'8px', height:'8px', borderRadius:'50%', background:'#ea4c3c'}}></div>
            <span style={{fontSize:'12px', textTransform:'uppercase', letterSpacing:'0.08em', color:'#888'}}>Projets en cours</span>
            <span style={{marginLeft:'auto', fontSize:'12px', color:'#bbb'}}>Mis à jour en temps réel</span>
          </div>
          <div style={{overflow:'hidden'}}>
            <div style={{display:'flex', gap:'12px', padding:'14px 20px', animation:'tickerScroll 32s linear infinite', width:'max-content'}}
                 onMouseEnter={e => (e.currentTarget.style.animationPlayState='paused')}
                 onMouseLeave={e => (e.currentTarget.style.animationPlayState='running')}>
              {[...projects, ...projects].map((p, i) => (
                <div key={i} style={{width:'240px', flexShrink:0, background:'#f9f9f9', border:'1px solid #e5e5e5', borderRadius:'8px', padding:'12px 14px'}}>
                  <div style={{fontSize:'13px', fontWeight:500, marginBottom:'6px', overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical'}}>{p.name}</div>
                  <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                    <span style={{fontSize:'11px', color:'#ea4c3c', border:'1px solid #ea4c3c', borderRadius:'4px', padding:'2px 8px'}}>{p.budget} €</span>
                    <span style={{fontSize:'11px', color:'#aaa'}}>Délai : {p.timeline} mois</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 20px', borderTop:'1px solid #e5e5e5'}}>
            <span style={{fontSize:'12px', color:'#888'}}>Vous avez un projet ? Publiez-le gratuitement.</span>
            <button style={{fontSize:'12px', color:'#ea4c3c', border:'1px solid #ea4c3c', borderRadius:'4px', padding:'4px 12px', background:'transparent', cursor:'pointer'}}>S'inscrire →</button>
          </div>
        </div>
      )}
      {/* Hero Section v2 */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '620px' }}>
        {/* Background image */}
        <img
          src="/hero-tradespeople.png"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover hero-bg-img"
        />
        {/* Dark gradient overlay */}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.45) 55%, rgba(0,0,0,0.82) 100%)' }}
        />
        {/* Text content pinned to bottom */}
        <div className="relative z-10 flex flex-col justify-end h-full" style={{ minHeight: '620px' }}>
          <div className="container mx-auto px-4 pb-14 sm:pb-20">
            <div className="max-w-2xl">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
                {t('home.hero.v2.title.line1')}<br />{t('home.hero.v2.title.line2')}
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-white/90 mb-6 text-left">
                {t('home.hero.v2.subtitle.line1')}<br />{t('home.hero.v2.subtitle.line2')}
              </p>

              {currentUser ? (
                <button
                  onClick={goToDashboard}
                  className="bg-white px-6 py-3 rounded-lg font-semibold text-base shadow-lg hover:bg-gray-100 transition-colors"
                  style={{ color: '#c70039' }}
                >
                  {t('nav.dashboard')}
                </button>
              ) : (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-3">
                    <a
                      href="/register?role=demandeur"
                      className="inline-block px-6 py-3 rounded-lg font-semibold text-base shadow-lg transition-colors"
                      style={{ background: '#c70039', color: '#fff' }}
                    >
                      {t('home.hero.v2.cta')}
                    </a>
                    <a
                      href="/register?role=prestataire"
                      className="inline-block border-2 border-white text-white px-6 py-3 rounded-lg font-semibold text-base hover:bg-white/10 transition-colors"
                    >
                      {t('home.hero.v2.cta2')}
                    </a>
                  </div>
                  <a
                    href="/login"
                    className="inline-block text-sm text-white/70 hover:text-white transition-colors underline underline-offset-2"
                  >
                    {t('home.hero.v2.login')}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-white border-b border-gray-100 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex justify-center py-5 px-3">
            <div className="flex flex-col items-center text-center">
              <span className="text-2xl sm:text-3xl font-black text-gray-900 leading-none mb-1">100%</span>
              <span className="text-xs sm:text-sm text-gray-500 font-medium">{t('home.stats.free')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* KPI Banner - Optimized for Mobile */}
      {!loadingKpi && visibleKpis.length > 0 && (
        <section className="bg-primary-600/50 backdrop-blur-sm py-4 sm:py-6 md:py-8">
          <div className="container mx-auto px-3 sm:px-4">
            <div className="text-center mb-3 sm:mb-4 md:mb-6">
              <h3 className="text-sm sm:text-base md:text-lg font-semibold text-white mb-1 sm:mb-2">Join Our Growing Platform</h3>
              <p className="text-xs sm:text-sm text-white/90">See what our community has achieved together</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 md:gap-4">
              {visibleKpis.map((kpi, index) => {
                const IconComponent = kpi.icon;
                return (
                  <div key={index} className="text-center">
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2 sm:p-3 md:p-4 mb-1 sm:mb-2 hover:bg-white/30 transition-colors">
                      <IconComponent className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-white mx-auto mb-1 sm:mb-2" />
                      <div className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-white">
                        {kpi.value.toLocaleString()}
                      </div>
                    </div>
                    <div className="text-xs sm:text-sm text-white/90 font-medium">
                      {kpi.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section id="about" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">{t('home.howItWorks')}</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <div className="flex justify-center mb-4">
                <Users className="h-12 w-12 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('home.forEntrepreneurs')}</h3>
              <p className="text-gray-700 mb-4">{t('home.forEntrepreneurs.desc')}</p>
              <a href="/register" className="text-primary-600 font-medium inline-flex items-center">
                {t('auth.register')} <ChevronRight className="h-4 w-4 ml-1" />
              </a>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <div className="flex justify-center mb-4">
                <BrainCircuit className="h-12 w-12 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('home.forSpecialists')}</h3>
              <p className="text-gray-700 mb-4">{t('home.forSpecialists.desc')}</p>
              <a href="/register" className="text-primary-600 font-medium inline-flex items-center">
                {t('auth.register')} <ChevronRight className="h-4 w-4 ml-1" />
              </a>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <div className="flex justify-center mb-4">
                <Shield className="h-12 w-12 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('home.secureConnection')}</h3>
              <p className="text-gray-700 mb-4">{t('home.secureConnection.desc')}</p>
              <a href="#subscription" className="text-primary-600 font-medium inline-flex items-center">
                {t('home.viewPlans')} <ChevronRight className="h-4 w-4 ml-1" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Subscription Plans */}
      <section id="subscription" className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4">{t('language') === 'fr' ? 'Plan d\'abonnement spécialiste IA' : 'AI Specialist Subscription Plan'}</h2>
          <p className="text-center text-gray-600 max-w-2xl mx-auto mb-12">
            {t('home.subscriptionPlans.desc')}
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Normal Plan */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-105 relative">
              {hasSpecialOffer(prices.normal) && (
                <div className="absolute top-4 right-4 z-10">
                  <div className="bg-red-500 text-white px-2 py-0.5 text-xs font-bold transform rotate-12 shadow-lg">
                    {t('businessPlan.specialOffer')}
                  </div>
                </div>
              )}
              <div className="bg-gray-100 py-4 px-6 text-left">
                <h3 className="text-xl font-bold">{t('businessPlan.normal')}</h3>
              </div>
              <div className="p-6">
                <div className="text-center mb-6">
                  <span className="text-3xl font-bold">€{prices.normal.promotional !== undefined ? prices.normal.promotional : prices.normal.regular}</span>
                  <span className="text-gray-600">{t('businessPlan.month')}</span>
                  {prices.normal.promotional !== undefined && prices.normal.promotional !== prices.normal.regular && (
                    <div className="text-sm text-gray-500 line-through">€{prices.normal.regular}/month</div>
                  )}
                </div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <ChevronRight className="h-5 w-5 text-primary-600 shrink-0 mt-0.5" />
                    <span>{t('businessPlan.features.normal.projects')}</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="h-5 w-5 text-primary-600 shrink-0 mt-0.5" />
                    <span>{t('businessPlan.features.normal.country')}</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="h-5 w-5 text-primary-600 shrink-0 mt-0.5" />
                    <span>{t('businessPlan.features.normal.visibility')}</span>
                  </li>
                </ul>
                <a 
                  href="/business-plan"
                  className="block w-full text-center bg-primary-600 text-white py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors"
                >
                  {t('businessPlan.getStarted')}
                </a>
              </div>
            </div>
            
            {/* Gold Plan */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-105 border-2 border-primary-600 relative">
              {hasSpecialOffer(prices.gold) && (
                <div className="absolute top-4 right-4 z-10">
                  <div className="bg-red-500 text-white px-2 py-0.5 text-xs font-bold transform rotate-12 shadow-lg">
                    {t('businessPlan.specialOffer')}
                  </div>
                </div>
              )}
              <div className="bg-primary-600 py-4 px-6 text-left text-white">
                <h3 className="text-xl font-bold">{t('businessPlan.gold')}</h3>
              </div>
              <div className="p-6">
                <div className="text-center mb-6">
                  <span className="text-3xl font-bold">€{prices.gold.promotional !== undefined ? prices.gold.promotional : prices.gold.regular}</span>
                  <span className="text-gray-600">{t('businessPlan.month')}</span>
                  {prices.gold.promotional !== undefined && prices.gold.promotional !== prices.gold.regular && (
                    <div className="text-sm text-gray-500 line-through">€{prices.gold.regular}/month</div>
                  )}
                </div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <ChevronRight className="h-5 w-5 text-primary-600 shrink-0 mt-0.5" />
                    <span>{t('businessPlan.features.gold.projects')}</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="h-5 w-5 text-primary-600 shrink-0 mt-0.5" />
                    <span>{t('businessPlan.features.gold.countries')}</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="h-5 w-5 text-primary-600 shrink-0 mt-0.5" />
                    <span>{t('businessPlan.features.gold.visibility')}</span>
                  </li>
                </ul>
                <a 
                  href="/business-plan"
                  className="block w-full text-center bg-primary-600 text-white py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors"
                >
                  {t('businessPlan.getStarted')}
                </a>
              </div>
            </div>
            
            {/* Platinum Plan */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-105 relative">
              {hasSpecialOffer(prices.platinum) && (
                <div className="absolute top-4 right-4 z-10">
                  <div className="bg-red-500 text-white px-2 py-0.5 text-xs font-bold transform rotate-12 shadow-lg">
                    {t('businessPlan.specialOffer')}
                  </div>
                </div>
              )}
              <div className="bg-gray-800 py-4 px-6 text-left text-white">
                <h3 className="text-xl font-bold">{t('businessPlan.platinum')}</h3>
              </div>
              <div className="p-6">
                <div className="text-center mb-6">
                  <span className="text-3xl font-bold">€{prices.platinum.promotional !== undefined ? prices.platinum.promotional : prices.platinum.regular}</span>
                  <span className="text-gray-600">{t('businessPlan.month')}</span>
                  {prices.platinum.promotional !== undefined && prices.platinum.promotional !== prices.platinum.regular && (
                    <div className="text-sm text-gray-500 line-through">€{prices.platinum.regular}/month</div>
                  )}
                </div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <ChevronRight className="h-5 w-5 text-primary-600 shrink-0 mt-0.5" />
                    <span>{t('businessPlan.features.platinum.projects')}</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="h-5 w-5 text-primary-600 shrink-0 mt-0.5" />
                    <span>{t('businessPlan.features.platinum.countries')}</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="h-5 w-5 text-primary-600 shrink-0 mt-0.5" />
                    <span>{t('businessPlan.features.platinum.support')}</span>
                  </li>
                </ul>
                <a 
                  href="/business-plan"
                  className="block w-full text-center bg-primary-600 text-white py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors"
                >
                  {t('businessPlan.getStarted')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <BrainCircuit className="h-8 w-8 text-primary-600" />
                <span className="text-xl font-semibold">OpportunAI</span>
              </div>
              <p className="text-gray-400">
                {t('home.footer.description')}
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">{t('home.footer.quickLinks')}</h3>
              <ul className="space-y-2">
                <li><a href="/" className="text-gray-400 hover:text-primary-600 transition-colors">{t('nav.home')}</a></li>
                <li><a href="/register" className="text-gray-400 hover:text-primary-600 transition-colors">{t('auth.register')}</a></li>
                <li><a href="/login" className="text-gray-400 hover:text-primary-600 transition-colors">{t('auth.login')}</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">{t('home.footer.contact')}</h3>
              <p className="text-gray-400">
                {t('home.footer.contactDesc')}
              </p>
              <a href="mailto:contact@opportunai.com" className="text-primary-600 hover:underline mt-2 inline-block">
                contact@opportunai.com
              </a>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} OpportunAI. {t('home.footer.rights')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;